/**
 * Admin Dashboard
 * Simplified dashboard matching the design
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authenticatedFetch } from '../lib/api';
import { useToast } from '../components/shared/Toast';
import { useLanguage } from '../context/LanguageContext';
import {
  erpPageBreadcrumbClass,
  erpPageBreadcrumbCurrentClass,
  erpPageBreadcrumbItemClass,
  erpPageBreadcrumbSepClass,
} from '@euroasiann/shared/ui';
import {
  MdRequestQuote,
  MdPeople,
  MdEmojiEmotions,
  MdStore,
  MdInventory,
  MdCategory,
  MdClose,
  MdDateRange,
  MdCalendarToday,
  MdClear,
} from 'react-icons/md';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

type DashboardStats = {
  totalRFQs: number;
  totalMerchants: number;
  totalCustomers: number;
  totalBrands: number;
  totalCategories: number;
  totalItemsSent: number;
  rfqsByMonth: Array<{ name: string; value: number }>;
  orgsByMonth: Array<{ name: string; visitors: number }>;
};

const EMPTY_STATS: DashboardStats = {
  totalRFQs: 0,
  totalMerchants: 0,
  totalCustomers: 0,
  totalBrands: 0,
  totalCategories: 0,
  totalItemsSent: 0,
  rfqsByMonth: [],
  orgsByMonth: [],
};

const CHART_ACCENT = '#6366f1';

const chartTooltipStyle: React.CSSProperties = {
  background: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 8,
  color: 'hsl(var(--foreground))',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
};

/** Area-chart hover: gradient band + vertical guide instead of a solid white rectangle. */
function AreaStyleHoverCursor({
  gradientId,
  points,
  x,
  y,
  width,
  height,
  top,
}: {
  gradientId: string;
  points?: Array<{ x: number; y: number }>;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  top?: number;
}) {
  const xPos =
    points?.[0]?.x ?? (typeof x === 'number' ? x + (Number(width) || 0) / 2 : undefined);
  if (xPos == null || Number.isNaN(xPos)) return null;

  const lineTop = typeof top === 'number' ? top : y ?? 0;
  const lineHeight = Number(height) || 0;
  if (lineHeight <= 0) return null;

  const bandWidth = Math.max(36, Math.min(64, Number(width) || 48));

  return (
    <g pointerEvents="none">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={CHART_ACCENT} stopOpacity={0.28} />
          <stop offset="55%" stopColor={CHART_ACCENT} stopOpacity={0.1} />
          <stop offset="100%" stopColor={CHART_ACCENT} stopOpacity={0} />
        </linearGradient>
      </defs>
      <rect
        x={xPos - bandWidth / 2}
        y={lineTop}
        width={bandWidth}
        height={lineHeight}
        fill={`url(#${gradientId})`}
      />
      <line
        x1={xPos}
        y1={lineTop}
        x2={xPos}
        y2={lineTop + lineHeight}
        stroke={CHART_ACCENT}
        strokeWidth={2}
        strokeLinecap="round"
      />
    </g>
  );
}

export function Dashboard() {
  const { showToast } = useToast();
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'admin',
  });

  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activePreset, setActivePreset] = useState<'all' | 'today' | 'last7' | 'last30' | 'thisMonth' | 'last6Months' | 'custom'>('all');

  const { data: dashboardStats, isLoading, isFetching } = useQuery({
    queryKey: ['admin-dashboard-stats', startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const queryString = params.toString();
      const url = `/api/v1/admin/dashboard/stats${queryString ? `?${queryString}` : ''}`;
      const response = await authenticatedFetch(url);
      if (!response.ok) throw new Error('Failed to load dashboard stats');
      const json = await response.json();
      return (json.data || EMPTY_STATS) as DashboardStats;
    },
  });

  const stats = dashboardStats ?? EMPTY_STATS;
  const monthlyData = stats.rfqsByMonth;
  const visitorData = stats.orgsByMonth;

  const formatToInputDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handlePresetChange = (preset: 'all' | 'today' | 'last7' | 'last30' | 'thisMonth' | 'last6Months') => {
    const now = new Date();
    if (preset === 'all') {
      setStartDate('');
      setEndDate('');
      setActivePreset('all');
      return;
    }
    if (preset === 'today') {
      const today = formatToInputDate(now);
      setStartDate(today);
      setEndDate(today);
      setActivePreset('today');
      return;
    }
    if (preset === 'last7') {
      const past = new Date(now);
      past.setDate(past.getDate() - 7);
      setStartDate(formatToInputDate(past));
      setEndDate(formatToInputDate(now));
      setActivePreset('last7');
      return;
    }
    if (preset === 'last30') {
      const past = new Date(now);
      past.setDate(past.getDate() - 30);
      setStartDate(formatToInputDate(past));
      setEndDate(formatToInputDate(now));
      setActivePreset('last30');
      return;
    }
    if (preset === 'thisMonth') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(formatToInputDate(startOfMonth));
      setEndDate(formatToInputDate(now));
      setActivePreset('thisMonth');
      return;
    }
    if (preset === 'last6Months') {
      const past = new Date(now);
      past.setMonth(past.getMonth() - 6);
      setStartDate(formatToInputDate(past));
      setEndDate(formatToInputDate(now));
      setActivePreset('last6Months');
      return;
    }
  };

  const handleStartDateChange = (val: string) => {
    setStartDate(val);
    setActivePreset('custom');
  };

  const handleEndDateChange = (val: string) => {
    setEndDate(val);
    setActivePreset('custom');
  };

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
    setActivePreset('all');
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      role: 'admin',
    });
  };

  const handleSubmit = async () => {
    try {
      const response = await authenticatedFetch('/api/v1/admin/users/invite', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          portalType: 'admin',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to invite admin');
      }

      showToast('Admin invitation sent successfully!', 'success');
      handleCloseModal();
    } catch (error: any) {
      showToast(error.message || 'Failed to invite admin', 'error');
    }
  };

  return (
    <div className="w-full min-h-screen p-8 space-y-8">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <nav className={`${erpPageBreadcrumbClass} mb-2`} aria-label="Breadcrumb">
            <span className={erpPageBreadcrumbItemClass}>{t('Admin')}</span>
            <span className={erpPageBreadcrumbSepClass}>/</span>
            <span className={erpPageBreadcrumbCurrentClass}>{t('Dashboard')}</span>
          </nav>
          <h1 className="text-3xl font-bold text-[hsl(var(--foreground))]">{t('Dashboard')}</h1>
        </div>
        <button
          onClick={handleOpenModal}
          className="self-start md:self-auto px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-lg transition-colors font-medium shadow-sm"
        >
          {t('Invite Admin')}
        </button>
      </div>

      {/* Date Range / Calendar Filter Section */}
      <div className="p-5 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm transition-all">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Title & Quick Presets */}
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center">
                <MdDateRange className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-[hsl(var(--foreground))]">
                    {t('Date Calendar Filter')}
                  </h3>
                  {(startDate || endDate) && (
                    <span className="text-xs px-2.5 py-0.5 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] font-semibold">
                      {t('Filtered')}
                    </span>
                  )}
                  {isFetching && !isLoading && (
                    <span className="text-xs text-[hsl(var(--muted-foreground))] animate-pulse">
                      ({t('Updating...')})
                    </span>
                  )}
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  {startDate || endDate
                    ? `${t('Filtering data from')} ${startDate || 'Start'} ${t('to')} ${endDate || 'Now'}`
                    : t('Filter statistics by starting date and ending date or select a quick preset')}
                </p>
              </div>
            </div>

            {/* Quick presets */}
            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: 'all', label: t('All Time') },
                { key: 'today', label: t('Today') },
                { key: 'last7', label: t('Last 7 Days') },
                { key: 'last30', label: t('Last 30 Days') },
                { key: 'thisMonth', label: t('This Month') },
                { key: 'last6Months', label: t('Last 6 Months') },
              ].map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => handlePresetChange(preset.key as any)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    activePreset === preset.key
                      ? 'bg-[hsl(var(--primary))] text-white shadow-sm'
                      : 'bg-[hsl(var(--muted))]/60 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))] border border-[hsl(var(--border))]'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Starting and Ending Date Pickers */}
          <div className="flex flex-wrap sm:flex-nowrap items-end gap-3 pt-2 lg:pt-0 border-t lg:border-t-0 border-[hsl(var(--border))]">
            <div className="flex flex-col gap-1 w-full sm:w-auto">
              <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
                <MdCalendarToday className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                {t('Starting Date')}
              </label>
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => handleStartDateChange(e.target.value)}
                className="px-3 py-2 text-sm border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all cursor-pointer"
              />
            </div>

            <div className="flex flex-col gap-1 w-full sm:w-auto">
              <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
                <MdCalendarToday className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                {t('Ending Date')}
              </label>
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => handleEndDateChange(e.target.value)}
                className="px-3 py-2 text-sm border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all cursor-pointer"
              />
            </div>

            {(startDate || endDate) && (
              <div className="flex flex-col justify-end w-full sm:w-auto">
                <button
                  type="button"
                  onClick={handleClearDates}
                  className="px-3 py-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] rounded-lg border border-[hsl(var(--border))] transition-colors flex items-center justify-center gap-1.5"
                  title={t('Clear Date Filter')}
                >
                  <MdClear className="w-4 h-4" />
                  <span>{t('Reset')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">{t('Total RFQs Received')}</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
                {isLoading ? '...' : stats.totalRFQs.toLocaleString()}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t('Updated Now')}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
              <MdRequestQuote className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">{t('Total Merchants')}</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
                {isLoading ? '...' : stats.totalMerchants.toLocaleString()}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t('Updated Now')}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
              <MdPeople className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">{t('Total Happy Customers')}</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
                {isLoading ? '...' : stats.totalCustomers.toLocaleString()}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t('Updated Now')}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-md">
              <MdEmojiEmotions className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">{t('Total Brands')}</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
                {isLoading ? '...' : stats.totalBrands.toLocaleString()}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t('Updated Now')}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-md">
              <MdStore className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">{t('Total Items Send')}</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
                {isLoading ? '...' : stats.totalItemsSent.toLocaleString()}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t('Updated Now')}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
              <MdInventory className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">{t('Total Categories')}</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
                {isLoading ? '...' : stats.totalCategories.toLocaleString()}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t('Updated Now')}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-md">
              <MdCategory className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[hsl(var(--foreground))]">{t('Analytics & Trends')}</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              {startDate || endDate
                ? `${t('Visualizing metrics for period:')} ${startDate || t('All past')} → ${endDate || t('Present')}`
                : t('Visualizing monthly RFQ distribution and organization growth')}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar Chart */}
          <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">{t('RFQs by Period')}</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {startDate || endDate
                  ? `${t('Filtered Range:')} ${startDate || t('Start')} – ${endDate || t('Now')}`
                  : t('Recent Months Overview')}
              </p>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  cursor={<AreaStyleHoverCursor gradientId="dashboardBarHover" />}
                  contentStyle={chartTooltipStyle}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                  itemStyle={{ color: CHART_ACCENT }}
                />
                <Bar dataKey="value" fill={CHART_ACCENT} radius={[8, 8, 0, 0]} activeBar={false} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
              <p>
                {monthlyData.length > 1 && monthlyData[monthlyData.length - 1].value > monthlyData[monthlyData.length - 2].value
                  ? `Trending up by ${((monthlyData[monthlyData.length - 1].value - monthlyData[monthlyData.length - 2].value) / Math.max(monthlyData[monthlyData.length - 2].value, 1) * 100).toFixed(1)}% in latest period`
                  : monthlyData.length > 1 && monthlyData[monthlyData.length - 1].value < monthlyData[monthlyData.length - 2].value
                    ? `Trending down by ${((monthlyData[monthlyData.length - 2].value - monthlyData[monthlyData.length - 1].value) / Math.max(monthlyData[monthlyData.length - 2].value, 1) * 100).toFixed(1)}% in latest period`
                    : 'Stable across recent periods'}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                {startDate || endDate ? `${t('Showing RFQs for filtered period')} (${monthlyData.length} ${t('months')})` : t('Showing RFQs for the last 6 months')}
              </p>
            </div>
          </div>

          {/* Area Chart */}
          <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">{t('Organizations Growth')}</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {startDate || endDate
                  ? `${t('Filtered Range:')} ${startDate || t('Start')} – ${endDate || t('Now')}`
                  : t('Showing total organizations growth')}
              </p>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={visitorData}>
                <defs>
                  <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_ACCENT} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={CHART_ACCENT} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  cursor={<AreaStyleHoverCursor gradientId="dashboardAreaHover" />}
                  contentStyle={chartTooltipStyle}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                  itemStyle={{ color: CHART_ACCENT }}
                />
                <Area
                  type="monotone"
                  dataKey="visitors"
                  stroke={CHART_ACCENT}
                  fillOpacity={1}
                  fill="url(#colorVisitors)"
                  activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2, fill: CHART_ACCENT }}
                />
              </AreaChart>
            </ResponsiveContainer>
            <div className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
              <p>
                {visitorData.length > 1 && visitorData[visitorData.length - 1].visitors > visitorData[visitorData.length - 2].visitors
                  ? `Trending up by ${((visitorData[visitorData.length - 1].visitors - visitorData[visitorData.length - 2].visitors) / Math.max(visitorData[visitorData.length - 2].visitors, 1) * 100).toFixed(1)}% in latest period`
                  : visitorData.length > 1 && visitorData[visitorData.length - 1].visitors < visitorData[visitorData.length - 2].visitors
                    ? `Trending down by ${((visitorData[visitorData.length - 2].visitors - visitorData[visitorData.length - 1].visitors) / Math.max(visitorData[visitorData.length - 2].visitors, 1) * 100).toFixed(1)}% in latest period`
                    : 'Stable across recent periods'}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                {startDate || endDate ? `${t('Showing organizations created for filtered period')} (${visitorData.length} ${t('months')})` : t('Showing organizations created for the last 6 months')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Admin Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-[hsl(var(--card))] rounded-lg shadow-xl w-full max-w-md mx-4 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--border))]">
              <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Invite Admin</h2>
              <button
                onClick={handleCloseModal}
                className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] dark:hover:text-gray-200"
              >
                <MdClose className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
                Enter the details to invite a new admin user.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--primary))] rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--primary))] rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--primary))] rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                    placeholder="Enter last name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--primary))] rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                  >
                    <option value="admin">Admin</option>
                    <option value="super-admin">Super Admin</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-[hsl(var(--border))]">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 border border-blue-600 text-[hsl(var(--primary))] rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-lg transition-colors"
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
